import { FormatPainterOutlined } from '@ant-design/icons'
import { useLockFn, useRequest } from 'ahooks'
import { Popconfirm, Tooltip, message } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Report, ReportExtra } from '@/schemas'

interface ReportAutoGenerateProps {
  report: Report
  onSuccess: () => void
  getLastWeekReport: () => Promise<ReportExtra[] | undefined>
}

export const ReportAutoGenerate: React.FC<ReportAutoGenerateProps> = ({ report, onSuccess, getLastWeekReport }) => {
  const { t } = useTranslation()
  const [visible, setVisible] = React.useState(false)

  const { run: upsert } = useRequest(
    (data: ReportExtra) => ({
      url: '/api/project/upsertProjectReportExtra',
      method: 'post',
      data: {
        selfId: report.selfId,
        reportId: report.id,
        id: data.id,
        field: data.field,
        content: data.content,
      },
    }),
    {
      manual: true,
      fetchKey: (d) => d.field,
    }
  )

  const hasExtras = report.extras.length > 0

  const onUpsert = useLockFn(async () => {
    const extras = await getLastWeekReport()
    if (!extras) {
      message.warn(t('report.missing'))
      return
    }
    if (extras.length === 0) {
      message.warn(t('report.nodata'))
      return
    }
    for (const extra of extras) {
      const currentExtra = report.extras.find((e) => e.field === extra.field)
      await upsert({ ...currentExtra, field: extra.field, content: extra.content } as ReportExtra)
    }
    onSuccess()
    message.success(t('pull success'))
  })

  return (
    <Tooltip title={t('report.pull')}>
      <Popconfirm
        title={t('report.cover')}
        trigger={['click']}
        visible={visible}
        onVisibleChange={(visible) => {
          if (hasExtras) {
            setVisible(visible)
          }
        }}
        onConfirm={() => onUpsert()}
      >
        <FormatPainterOutlined
          className='cursor-pointer text-blue-600'
          onClick={() => {
            if (!hasExtras) {
              onUpsert()
            }
          }}
        />
      </Popconfirm>
    </Tooltip>
  )
}
